import { Fragment, type ResolvedPos, type Schema } from '@tiptap/pm/model';
import { TextSelection, type Transaction } from '@tiptap/pm/state';
import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';
import { buildDraftSectionContent } from './insert-draft-section';
import { splitContentAtLineBreak } from './split-content-at-line-break';

/**
 * Splits the section containing `$paragraph` into two siblings: blocks before
 * the trigger line stay put; the trigger line and everything after it move into
 * a new sibling section below, with the trigger's text as its heading. A real
 * sibling section avoids a nested `<h1>` that `stripSectionWrappers` would
 * flatten back to a top-level `# ` on reload.
 */
export function splitSectionAtParagraph(
  tr: Transaction,
  $paragraph: ResolvedPos,
  schema: Schema
): void {
  const { heading, paragraph, sectionNode } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) return;

  const sectionDepth = $paragraph.depth - 1;
  const section = $paragraph.node(sectionDepth);
  const sectionStart = $paragraph.before(sectionDepth);
  const sectionEnd = $paragraph.after(sectionDepth);
  const triggerIndex = $paragraph.index(sectionDepth);

  // Blocks that stay behind. A section needs a body block, so pad an empty
  // paragraph when nothing but the heading would remain.
  const stayingBlocks = [];
  for (let i = 0; i < triggerIndex; i++) stayingBlocks.push(section.child(i));
  if (stayingBlocks.length <= 1) stayingBlocks.push(paragraph.createChecked());
  const originalSection = sectionNode.createChecked(
    section.attrs,
    Fragment.fromArray(stayingBlocks)
  );

  // The trigger line plus everything after it forms the new sibling section.
  const triggerParagraph = section.child(triggerIndex);
  const movingBody = [];
  for (let i = triggerIndex + 1; i < section.childCount; i++) {
    movingBody.push(section.child(i));
  }
  // A `**Header**\nbody` trigger line with no blank line between is one
  // paragraph joined by a line break (soft `\n` or hard break). Only the
  // pre-break text is the heading; the rest leads the moved body instead of
  // being pulled into the title.
  const { headingContent, bodyContent } = splitContentAtLineBreak(
    triggerParagraph.content,
    schema.nodes.hardBreak
  );
  const carriedBody =
    bodyContent && bodyContent.size > 0
      ? [paragraph.createChecked(null, bodyContent), ...movingBody]
      : movingBody;
  const hasHeadingText = headingContent.size > 0;

  let newSection;
  if (!hasHeadingText && carriedBody.length === 0) {
    // Empty line with nothing to carry: a plain draft section, auto-removed on
    // blur if left untouched — same lifecycle as the slash-command "New Section".
    newSection = schema.nodeFromJSON(buildDraftSectionContent());
  } else {
    // An empty line that carries content down keeps the sentinel in its heading
    // so it stays parseable and the moved body is not orphaned on reload.
    const newHeading = heading.createChecked(
      { level: 1 },
      hasHeadingText ? headingContent : schema.text(DRAFT_SECTION_SENTINEL)
    );
    const newBody = carriedBody.length > 0 ? carriedBody : [paragraph.createChecked()];
    newSection = sectionNode.createChecked(
      {
        sectionTitle: hasHeadingText ? newHeading.textContent || null : 'New Section',
        isDraftSection: false,
      },
      Fragment.fromArray([newHeading, ...newBody])
    );
  }

  tr.replaceWith(
    sectionStart,
    sectionEnd,
    Fragment.fromArray([originalSection, newSection])
  );

  // Land the cursor inside the new heading: +1 enters the section, +1 the heading.
  const newSectionStart = sectionStart + originalSection.nodeSize;
  tr.setSelection(TextSelection.create(tr.doc, newSectionStart + 2));
}
